import {
  Model,
  UIField,
  UIFormatTemplateSelection,
  UIIconButton,
  useEffectAsync,
} from "@alphascriptsolutions/client-core";
import * as React from "react";
import { message } from "antd";
import Renderer from "@/components/Renderer";
import { formatTemplatesTablePanelSchema } from "@/utils/sharePricePerformance/panelsSchema";
import { fetchPrimaryTemplateWithImage } from "@/utils/sharePricePerformance/fetchFormatTemplates";
import { getSelectedShapeName } from "@/utils/sharePricePerformance/selectedPlaceHolderLocalStorage";
import { setSelectedShape } from "@/utils/sharePricePerformance/selectedPlaceHolderLocalStorage";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import { addFieldsToData, transformGlobalState } from "@/utils/alphaAgent/modelDataHelper";
import { safeParse } from "@/utils/alphaAgent/getRenderAgentData";
import { logger } from "@/helpers/tslog";
import { bufferToBase64ImagePrimaryTemplate } from "@/utils/formatTemplate/slides-extraction-handlers";
import { useState } from "react";
import FormatTemplateSearchPopup from "@/alphaPopup/components/FormatTemplateSearchPopup";
import { getFormatAndPlaceholderValues } from "@/utils/alphaAgent/FormatAndPlaceholderUtils";

export default function FormatAndPlaceholder() {
  const { model, setModel } = useAlphaAgentStore();
  const [templateSearchVisible, setTemplateSearchVisible] = useState(false);
  const [currentPrimaryTemplate, setCurrentPrimaryTemplate] = useState<any>(null);
  const rawData = useAlphaAgentStore((state) => state.documentData);
  const documentContent = safeParse(rawData.documentContent);

  useEffectAsync(async () => {
    const path = "/assets/alphaAgent/AlphaAgentFormatTemplate.yml";

    try {
      let loadedModel = await Model.fromFile(path);
      if (!loadedModel.isValid()) {
        throw new Error(`Problem loading model from ${path}: ${loadedModel.getError()}`);
      }

      await addEventsOnModel(loadedModel);
      setModel(loadedModel);
    } catch (err) {
      message.error(err.message);
    }
  }, []);

  const addEventsOnModel = async (model: Model) => {
    model.setSchema(formatTemplatesTablePanelSchema);

    let primaryFormatWithImage;

    if (documentContent.formatId || documentContent.customFormatId) {
      const formatId = documentContent.formatId || documentContent.customFormatId;
      try {
        primaryFormatWithImage = await fetchPrimaryTemplateWithImage(formatId);
        if (primaryFormatWithImage) {
          logger.info("Fetched template by ID from documentContent:", primaryFormatWithImage.formatName);
        } else {
          logger.info("Template not found by ID, falling back to primary template");
          primaryFormatWithImage = await fetchPrimaryTemplateWithImage();
        }
      } catch (error) {
        logger.error("Error fetching template by ID:", error);
        logger.info("Falling back to primary template");
        primaryFormatWithImage = await fetchPrimaryTemplateWithImage();
      }
    } else {
      logger.info("No format ID in documentContent, fetching primary template");
      primaryFormatWithImage = await fetchPrimaryTemplateWithImage();
    }

    const presentationDesign = model.getByName("presentationDesign");
    if (presentationDesign) {
      try {
        // Check if there's a saved template selection
        const savedTemplate = localStorage.getItem("selectedFormatTemplate");
        let templateToShow = primaryFormatWithImage;
        let savedImage = null;

        if (savedTemplate) {
          try {
            const parsedTemplate = JSON.parse(savedTemplate);
            if (parsedTemplate.id && parsedTemplate.formatName) {
              // Use the saved template data for display
              templateToShow = {
                ...primaryFormatWithImage,
                id: parsedTemplate.id,
                formatName: parsedTemplate.formatName,
              };
              savedImage = parsedTemplate.image;
              logger.info("Restored saved template selection:", parsedTemplate.formatName);
            }
          } catch (parseError) {
            logger.error("Error parsing saved template:", parseError);
          }
        }

        if (templateToShow) {
          if (templateToShow.formatName) {
            (presentationDesign as any).templateName = templateToShow.formatName;
          }
          (presentationDesign as any).value = templateToShow.id;

          if (savedImage) {
            (presentationDesign as any).image = savedImage;
          } else if (templateToShow.frontCoverImage?.buffer) {
            try {
              (presentationDesign as any).image = bufferToBase64ImagePrimaryTemplate(
                templateToShow.frontCoverImage.buffer,
                templateToShow.frontCoverImage.contentType
              );
            } catch (e: any) {
              (presentationDesign as any).image = undefined;
            }
          } else {
            logger.info("No frontcover image found");
            (presentationDesign as any).image = undefined;
          }

          (presentationDesign as any).onClick = () => {
            setTemplateSearchVisible(true);
          };

          setCurrentPrimaryTemplate(templateToShow);
          localStorage.removeItem("selectedFormatTemplate");
        }
      } catch (error) {
        logger.error("Error populating format template selection:", error);
      }
    } else {
      logger.info("No presentationDesign component found");
    }

    const placeholderSelectionName = model.getByName<UIField>("placeholderSelectionName");
    const selectedPlaceholderName = getSelectedShapeName();
    placeholderSelectionName.value = selectedPlaceholderName ?? "";
    if (selectedPlaceholderName) {
      const store = useAlphaAgentStore.getState();
      store.markAnswered(store.currentStep);
    }

    // If AlphaEditSelection exists, set it as the current placeholder and update field
    try {
      const alphaEditSelectionRaw = localStorage.getItem("AlphaEditSelection");
      if (alphaEditSelectionRaw) {
        const alphaEditSelection = JSON.parse(alphaEditSelectionRaw);
        if (alphaEditSelection && alphaEditSelection.name) {
          await setSelectedShape(alphaEditSelection);
          placeholderSelectionName.value = alphaEditSelection.name || "";
          const store = useAlphaAgentStore.getState();
          store.markAnswered(store.currentStep);
        }
      }
    } catch (e) {
      logger.warn("Failed to apply AlphaEditSelection on load", e);
    }

    const placeholderSelectionBtn = model.getByName<UIIconButton>("placeholderSelectionButton");
    if (placeholderSelectionBtn) {
      placeholderSelectionBtn.onClick = () => {
        const rawData = localStorage.getItem("alphaCreateData");
        const globalStateData = JSON.parse(rawData);
        const convertedDataToRequiredForm = transformGlobalState(globalStateData);
        const { model } = useAlphaAgentStore.getState();

        const presentationDesign = model.getByName<UIFormatTemplateSelection>("presentationDesign");
        const { data } = useAlphaAgentStore.getState();

        const { title, subTitle, source, notes } = getFormatAndPlaceholderValues();

        const completeData = addFieldsToData({
          chartObject: convertedDataToRequiredForm,
          customFormatId: presentationDesign.value,
          metadata: {
            chartTitle: {
              enabled: true,
              value: title,
            },
            chartSubTitle: {
              enabled: true,
              value: subTitle,
            },
            source: {
              enabled: true,
              value: source,
            },
            notes: {
              enabled: true,
              value: notes,
            },
          },
          tradingVolume: data.addsecondaryVerticalAxisColumnChart,
        });
        localStorage.setItem("ModelDataToBackend", JSON.stringify(completeData));
        localStorage.setItem("TradingVolumnAlphaEdit", String(data.addsecondaryVerticalAxisColumnChart));
        localStorage.setItem("AlphaAgentEditPlaceholder", "true");
        Office.context.ui.messageParent("OPEN_PLACEHOLDER");
      };
    }
  };

    // React.useEffect(() => {
    //   (async () => {
    //     if (currentStep === 3 && model) {
    //       logger.info("[FormatAndPlaceholder] Step 4 active: refreshing placeholders from alphaCreateData");
    //       // await populatePlaceholderFieldsFromAlphaCreateData(model as any);
    //     try {
    //       const rawData = localStorage.getItem("ModelDataToBackend");
    //       const { formatAndPlaceholderDatasource } = await getRenderAgentData(rawData);
    //       fieldRestorationUtils.restoreSourceNotesAlphaEdit(
    //         model as any,
    //         setModel,
    //         documentContent,
    //         {
    //           formatTemplateSource: formatAndPlaceholderDatasource?.formatTemplateSource,
    //           formatTemplateNotes: formatAndPlaceholderDatasource?.formatTemplateNotes,
    //         }
    //       );
    //     } catch (_) {}
    //     }
    //   })();
    // }, [currentStep, model]);
  

  const handleTemplateSelect = async (selectedTemplate: any) => {
    try {
      logger.info(`Template selected: ${selectedTemplate.formatName}`);

      const presentationDesign = model.getByName("presentationDesign");
      if (presentationDesign && selectedTemplate) {
        if (selectedTemplate.formatName) {
          (presentationDesign as any).templateName = selectedTemplate.formatName;
        }
        (presentationDesign as any).value = selectedTemplate.id;
        if (selectedTemplate.frontCoverImage?.buffer) {
          try {
            const getImageSrc = bufferToBase64ImagePrimaryTemplate(
              selectedTemplate.frontCoverImage.buffer,
              selectedTemplate.frontCoverImage.contentType
            );
            (presentationDesign as any).image = getImageSrc;
          } catch (conversionError) {
            logger.error("Error converting image:", conversionError);
            (presentationDesign as any).image = undefined;
          }
        } else {
          (presentationDesign as any).image = undefined;
        }

        setCurrentPrimaryTemplate(selectedTemplate);
        setModel(model);

        // Store the selected template in localStorage for persistence
        localStorage.setItem(
          "selectedFormatTemplate",
          JSON.stringify({
            id: selectedTemplate.id,
            formatName: selectedTemplate.formatName,
            image: selectedTemplate.frontCoverImage?.buffer
              ? bufferToBase64ImagePrimaryTemplate(
                  selectedTemplate.frontCoverImage.buffer,
                  selectedTemplate.frontCoverImage.contentType
                )
              : undefined,
          })
        );

        logger.info("Template updated successfully in presentation design and saved to localStorage");
      }
    } catch (error) {
      logger.error("Error handling template selection:", error);
    }
  };

  if (!model) return null;

  return (
    <div className="mt-6 p-4">
      <Renderer model={model} />

      <FormatTemplateSearchPopup
        visible={templateSearchVisible}
        onClose={() => setTemplateSearchVisible(false)}
        onTemplateSelect={handleTemplateSelect}
        currentPrimaryTemplateId={currentPrimaryTemplate?.id}
      />
    </div>
  );
}
